import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Badge, Form, InputGroup, Dropdown, Spinner, Alert } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchEvents, deleteEvent, Event, EventStatus } from '../redux/slices/eventsSlice';
import { eventsAPI } from '../services/eventsApi';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_META: Record<EventStatus, { label: string; color: string; bg: string }> = {
  draft:       { label: 'Draft',       color: '#92400e', bg: '#fef3c7' },
  published:   { label: 'Published',   color: '#065f46', bg: '#d1fae5' },
  upcoming:    { label: 'Upcoming',    color: '#1e40af', bg: '#dbeafe' },
  in_progress: { label: 'Live',        color: '#7c3aed', bg: '#ede9fe' },
  completed:   { label: 'Completed',   color: '#374151', bg: '#f3f4f6' },
  cancelled:   { label: 'Cancelled',   color: '#991b1b', bg: '#fee2e2' },
  postponed:   { label: 'Postponed',   color: '#92400e', bg: '#fff7ed' },
};

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const formatTime = (d: string) =>
  new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

// ─── Component ────────────────────────────────────────────────────────────────

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { events, pagination, loading, error } = useAppSelector(s => s.events);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    dispatch(fetchEvents({
      search: search || undefined,
      status: statusFilter as EventStatus || undefined,
      page,
      limit: 20,
    }));
  }, [dispatch, search, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (e: React.MouseEvent, id: string, title: string) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setDeletingId(id);
    setDeleteError('');
    try {
      await dispatch(deleteEvent(id)).unwrap();
    } catch (err: any) {
      setDeleteError(typeof err === 'string' ? err : (err?.message || 'Failed to delete event'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleExportCSV = async (e: React.MouseEvent, eventId: string, slug: string) => {
    e.stopPropagation();
    try {
      const res = await eventsAPI.exportRegistrations(eventId);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug}-registrations.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  const allStatuses: EventStatus[] = ['draft','published','upcoming','in_progress','completed','cancelled','postponed'];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fc', paddingTop: '32px', paddingBottom: '60px' }}>
      <Container fluid style={{ maxWidth: 1300 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Events</h1>
            <p style={{ color: '#64748b', margin: 0, fontSize: 14 }}>
              {pagination.total} event{pagination.total !== 1 ? 's' : ''} total
            </p>
          </div>
          <button
            onClick={() => navigate('/events/new')}
            style={{
              background: '#00579a', color: '#fff', border: 'none',
              padding: '10px 20px', borderRadius: 8, fontWeight: 600,
              fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <span style={{ fontSize: 18 }}>+</span> New Event
          </button>
        </div>

        {deleteError && <Alert variant="danger" dismissible onClose={() => setDeleteError('')}>{deleteError}</Alert>}

        {/* Filters */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: '16px 20px',
          marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <InputGroup style={{ maxWidth: 300 }}>
            <InputGroup.Text style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRight: 'none' }}>
              🔍
            </InputGroup.Text>
            <Form.Control
              placeholder="Search events..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{ border: '1px solid #e2e8f0', borderLeft: 'none', fontSize: 14 }}
            />
          </InputGroup>

          <Form.Select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ maxWidth: 180, fontSize: 14, border: '1px solid #e2e8f0' }}
          >
            <option value="">All Statuses</option>
            {allStatuses.map(s => (
              <option key={s} value={s}>{STATUS_META[s].label}</option>
            ))}
          </Form.Select>

          {(search || statusFilter) && (
            <button
              onClick={() => { setSearch(''); setStatusFilter(''); setPage(1); }}
              style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer', color: '#64748b' }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Spinner animation="border" style={{ color: '#00579a' }} />
          </div>
        ) : events.length === 0 ? (
          <div style={{
            background: '#fff', borderRadius: 12, padding: '80px 40px', textAlign: 'center',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📅</div>
            <h3 style={{ color: '#1e293b', marginBottom: 8 }}>No events found</h3>
            <p style={{ color: '#94a3b8', marginBottom: 24 }}>
              {search || statusFilter ? 'Try adjusting your filters.' : 'Create your first event to get started.'}
            </p>
            {!search && !statusFilter && (
              <button
                onClick={() => navigate('/events/new')}
                style={{ background: '#00579a', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
              >
                Create Event
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {events.map(event => (
                <EventRow
                  key={event.id}
                  event={event}
                  deleting={deletingId === event.id}
                  onView={() => navigate(`/events/${event.id}`)}
                  onEdit={e => { e.stopPropagation(); navigate(`/events/${event.id}/edit`); }}
                  onDelete={e => handleDelete(e, event.id, event.title)}
                  onRegistrations={e => { e.stopPropagation(); navigate(`/events/${event.id}/registrations`); }}
                  onExport={e => handleExportCSV(e, event.id, event.slug)}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 32 }}>
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    style={{
                      width: 36, height: 36, borderRadius: 8, border: 'none',
                      background: p === page ? '#00579a' : '#fff',
                      color: p === page ? '#fff' : '#374151',
                      fontWeight: p === page ? 700 : 400,
                      cursor: 'pointer', fontSize: 14,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

// ─── Event Row Card ───────────────────────────────────────────────────────────

interface EventRowProps {
  event: Event;
  deleting: boolean;
  onView: () => void;
  onEdit: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  onRegistrations: (e: React.MouseEvent) => void;
  onExport: (e: React.MouseEvent) => void;
}

const EventRow: React.FC<EventRowProps> = ({ event, deleting, onView, onEdit, onDelete, onRegistrations, onExport }) => {
  const meta = STATUS_META[event.status] || STATUS_META.draft;
  const isPast = new Date(event.start_date) < new Date();

  return (
    <div
      onClick={onView}
      style={{
        background: '#fff',
        borderRadius: 10,
        padding: '16px 20px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        transition: 'box-shadow 0.15s',
        opacity: deleting ? 0.5 : 1,
        borderLeft: `4px solid ${meta.color}`,
      }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)')}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)')}
    >
      {/* Date block */}
      <div style={{
        minWidth: 56, textAlign: 'center',
        background: '#f8f9fc', borderRadius: 8, padding: '8px 4px',
      }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
          {new Date(event.start_date).getDate()}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
          {new Date(event.start_date).toLocaleString('en', { month: 'short' })}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>
          {new Date(event.start_date).getFullYear()}
        </div>
      </div>

      {/* Main info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {event.title}
          </span>
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
            color: meta.color, background: meta.bg, whiteSpace: 'nowrap',
          }}>
            {meta.label}
          </span>
          {event.is_virtual && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, color: '#6366f1', background: '#eef2ff', whiteSpace: 'nowrap' }}>
              Virtual
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {event.venue_name && <span>📍 {event.venue_name}</span>}
          <span>🕐 {formatTime(event.start_date)}{event.end_date ? ` – ${formatTime(event.end_date)}` : ''}</span>
          {event.category && <span>🏷 {event.category}</span>}
          {!event.is_free && event.price && (
            <span>💰 {event.currency} {Number(event.price).toFixed(2)}</span>
          )}
        </div>
      </div>

      {/* Stats */}
      {event.registration_required && (
        <div style={{ textAlign: 'center', minWidth: 64 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#00579a' }}>
            {event.registration_count ?? 0}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            {event.capacity ? `/ ${event.capacity}` : 'registered'}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
        {event.registration_required && (
          <button
            onClick={onRegistrations}
            title="View Registrations"
            style={{
              background: '#f1f5f9', border: 'none', borderRadius: 6,
              padding: '6px 10px', fontSize: 13, cursor: 'pointer', color: '#374151',
            }}
          >
            👥
          </button>
        )}
        <button
          onClick={onExport}
          title="Export CSV"
          style={{
            background: '#f1f5f9', border: 'none', borderRadius: 6,
            padding: '6px 10px', fontSize: 13, cursor: 'pointer', color: '#374151',
          }}
        >
          ⬇
        </button>
        <button
          onClick={onEdit}
          style={{
            background: '#f1f5f9', border: 'none', borderRadius: 6,
            padding: '6px 12px', fontSize: 13, cursor: 'pointer', color: '#374151', fontWeight: 500,
          }}
        >
          Edit
        </button>
        <button
          onClick={onDelete}
          disabled={deleting}
          style={{
            background: '#fff0f0', border: 'none', borderRadius: 6,
            padding: '6px 12px', fontSize: 13, cursor: 'pointer', color: '#dc2626', fontWeight: 500,
          }}
        >
          {deleting ? '...' : 'Delete'}
        </button>
      </div>
    </div>
  );
};

export default EventsPage;